import React, { useRef, useEffect, useMemo } from 'react';
import Konva from 'konva';
import { Rect, Ellipse, Image as KonvaImage, Group } from 'react-konva';
import useImage from 'use-image';
import { useCacheBustedImage } from '../../hooks/useCacheBustedImage';
import { elementRegistry, RenderContext } from '../registry';
import { useTemplateStore } from './useTemplateStore';
import { EditableText } from '../components/editable-text';
import { ResolveContext, resolveImageSource, resolveStaticOrDynamicWithPreview } from '../../core/runtime/resolve';
import { SvgRenderer } from '../components/renderers/SvgRenderer';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Permission } from '@/lib/api/interfaces/permissions.interface';

const DEFAULT_PATH = "/defaults/image_placeholder.png";

const hexToRgba = (hex: string, alpha: number) => {
    let c: any;
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
        c= hex.substring(1).split('');
        if(c.length === 3){
            c= [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c= '0x'+c.join('');
        return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+','+alpha+')';
    }
    // Fallback if not hex (e.g. named color or already rgba)
    if (hex.startsWith('rgba')) {
      // replace alpha? Too complex for now, assume hex was passed primarily.
      return hex;
    }
    return hex;
}

/**
 * Hook to handle caching for elements with filters (like Blur).
 * Uses useEffect to avoid re-caching on every render, which causes performance issues and transformer desync.
 */
const useSmartCache = (nodeRef: React.RefObject<any>, config: any) => {
  const hasBlur = config.filters && config.filters.length > 0;
  
  useEffect(() => {
    const node = nodeRef.current;
    if (node) {
      if (hasBlur) {
        // Fix for Clipping: Calculate padding to ensure the blur (and shadow) fits in the cache
        const blurRadius = config.blurRadius || 0;
        const shadowBlur = config.shadowBlur || 0;
        const shadowX = config.shadowOffsetX ? Math.abs(config.shadowOffsetX) : 0;
        const shadowY = config.shadowOffsetY ? Math.abs(config.shadowOffsetY) : 0;
        
        const padding = Math.max(blurRadius * 4, shadowBlur * 2 + Math.max(shadowX, shadowY)) + 20;
        
        node.cache({
            offset: padding
        });
      } else {
        node.clearCache();
      }
    }
  }, [hasBlur, config.blurRadius, config.width, config.height, config.fill, config.stroke, config.shadowColor, config.shadowBlur, config.shadowOpacity, config.cornerRadius, nodeRef, config.shadowOffsetX, config.shadowOffsetY, config.offsetX, config.offsetY, config._imageStatus]);
};

/**
 * Generic Renderer for Shapes (Rect, Circle) to support consistent LayerBlur and caching
 */
const ShapeRenderer = ({ ShapeComponent, config }: { ShapeComponent: any, config: any }) => {
  const nodeRef = useRef<any>(null);
  
  // Stabilize filters array to prevent unnecessary re-renders/effect triggers
  const hasBlur = config.filters && config.filters.length > 0;
  const filters = useMemo(() => hasBlur ? [Konva.Filters.Blur] : [], [hasBlur]);
  
  const finalProps = {
    ...config,
    filters,
    ref: nodeRef
  };
  
  useSmartCache(nodeRef, finalProps);
  
  return React.createElement(ShapeComponent, finalProps);
};

function ImageElementRenderer({
  element,
  enhancedConfig,
  resolveContext,
  eventHandlers,
}: {
  element: any;
  enhancedConfig: any;
  resolveContext: ResolveContext;
  eventHandlers: {
    onDragMove: (e: any) => void;
    onDragEnd: (e: any) => void;
    onTransform: (e: any) => void;
    onTransformEnd: (e: any) => void;
  };
}) {
  const visualGroupRef = useRef<any>(null);
  const clippedGroupRef = useRef<any>(null);
  const imageUrl = resolveImageSource(element.source, resolveContext);

  const [image, status] = useCacheBustedImage(imageUrl, 'anonymous');
  const [fallbackImage] = useImage(DEFAULT_PATH, 'anonymous');

  const img = status === "loaded" ? image : fallbackImage;

  // Determine fit
  const fit: "fill" | "contain" | "cover" | "none" = (element.source?.format?.fit as any) || "contain";

  // Stabilize filters for visual group (Moved up for Hook rules)
  const hasBlur = enhancedConfig.filters && enhancedConfig.filters.length > 0;
  const filters = useMemo(() => hasBlur ? [Konva.Filters.Blur] : [], [hasBlur]);

  const containerWidth = element.width || 0;
  const containerHeight = element.height || 0;

  // Visual Group Props (Effects) - CACHED (Moved up for Hook rules)
  // Shadow is always applied to the image node now, so we don't apply it here.
  const visualGroupProps = {
    width: containerWidth,
    height: containerHeight,
    filters,
    blurRadius: enhancedConfig.blurRadius,
    shadowColor: undefined,
    shadowBlur: undefined,
    shadowOffsetX: undefined,
    shadowOffsetY: undefined,
    shadowOpacity: undefined,
    ref: visualGroupRef,
    listening: false, // Interactions on Root or DummyRect
  };
  
  // Use smart caching on the Visual Group
  // Add image status to dependencies to re-cache when image loads
  useSmartCache(visualGroupRef, {
    ...visualGroupProps,
    shadowColor: enhancedConfig.shadowColor,
    shadowBlur: enhancedConfig.shadowBlur,
    shadowOffsetX: enhancedConfig.shadowOffsetX,
    shadowOffsetY: enhancedConfig.shadowOffsetY,
    shadowOpacity: enhancedConfig.shadowOpacity,
    // Add dummy prop to force update on image load
    _imageStatus: status 
  });

  if (!img || !element.width || !element.height || element.width <= 0 || element.height <= 0) {
    return null;
  }

  const imageWidth = img.width;
  const imageHeight = img.height;
  const overflow: "hidden" | "visible" = element.overflow || "hidden";

  let drawWidth: number;
  let drawHeight: number;
  let drawX: number;
  let drawY: number;

  switch (fit) {
    case "fill":
      drawWidth = containerWidth;
      drawHeight = containerHeight;
      drawX = 0;
      drawY = 0;
      break;

    case "contain": {
      const scale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
      drawWidth = imageWidth * scale;
      drawHeight = imageHeight * scale;
      drawX = (containerWidth - drawWidth) / 2;
      drawY = (containerHeight - drawHeight) / 2;
      break;
    }

    case "none":
      drawWidth = imageWidth;
      drawHeight = imageHeight;
      drawX = (containerWidth - drawWidth) / 2;
      drawY = (containerHeight - drawHeight) / 2;
      break;

    case "cover":
    default: {
      const scale = Math.max(containerWidth / imageWidth, containerHeight / imageHeight);
      drawWidth = imageWidth * scale;
      drawHeight = imageHeight * scale;
      drawX = (containerWidth - drawWidth) / 2;
      drawY = (containerHeight - drawHeight) / 2;
      break;
    }
  }

  // Use top-left inner group; avoid centered offsets to prevent 50% shift
  const innerGroupConfig: any = {
    x: 0,
    y: 0,
    offsetX: 0,
    offsetY: 0,
    width: containerWidth,
    height: containerHeight,
  };

  // Determine clipping behavior
  const shouldClip = (element.clipRadius && element.clipRadius > 0) || 
                     (overflow === "hidden");

  if (shouldClip) {
    innerGroupConfig.clipFunc = (ctx: any) => {
      const radius = Math.min(
        element.clipRadius || 0,
        containerWidth / 2,
        containerHeight / 2
      );
      const w = containerWidth;
      const h = containerHeight;

      if (radius > 0) {
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.lineTo(w - radius, 0);
        ctx.quadraticCurveTo(w, 0, w, radius);
        ctx.lineTo(w, h - radius);
        ctx.quadraticCurveTo(w, h, w - radius, h);
        ctx.lineTo(radius, h);
        ctx.quadraticCurveTo(0, h, 0, h - radius);
        ctx.lineTo(0, radius);
        ctx.quadraticCurveTo(0, 0, radius, 0);
        ctx.closePath();
      } else {
        ctx.beginPath();
        ctx.rect(0, 0, w, h);
        ctx.closePath();
      }
    };
  } else {
    innerGroupConfig.clipFunc = undefined;
  }

  // Image now uses top-left coordinates directly
  const imageConfig = {
    image: img,
    x: drawX,
    y: drawY,
    width: drawWidth,
    height: drawHeight,
    // Always apply shadow to the image as requested
    shadowColor: enhancedConfig.shadowColor,
    shadowBlur: enhancedConfig.shadowBlur,
    shadowOffsetX: enhancedConfig.shadowOffsetX,
    shadowOffsetY: enhancedConfig.shadowOffsetY,
    shadowOpacity: enhancedConfig.shadowOpacity,
  };

  // Root Props (Positioning & Events) - NO CACHE
  const rootProps = {
    x: enhancedConfig.x,
    y: enhancedConfig.y,
    offsetX: enhancedConfig.offsetX,
    offsetY: enhancedConfig.offsetY,
    rotation: enhancedConfig.rotation,
    opacity: enhancedConfig.opacity,
    draggable: enhancedConfig.draggable,
    onClick: enhancedConfig.onClick,
    onTap: enhancedConfig.onTap,
    onDragMove: enhancedConfig.onDragMove,
    onDragEnd: enhancedConfig.onDragEnd,
    onContextMenu: enhancedConfig.onContextMenu,
    id: enhancedConfig.id, // ID on Root for selection? No, Transformer targets DummyRect
  };

  return React.createElement(
    Group,
    rootProps,
    // Visuals (Cached)
    React.createElement(
      Group,
      visualGroupProps,
      React.createElement(
        Group,
        { ...innerGroupConfig, ref: clippedGroupRef },
        React.createElement(KonvaImage, imageConfig)
      )
    ),
    // Interaction Target (Uncached sibling)
    React.createElement(Rect, {
      id: `${element.id}-transform-target`,
      x: containerWidth / 2,
      y: containerHeight / 2,
      offsetX: containerWidth / 2,
      offsetY: containerHeight / 2,
      width: containerWidth,
      height: containerHeight,
      listening: true,
      draggable: false,
      fill: 'rgba(0,0,0,0)',
      onTransform: (e: any) => {
        // Critical: Clear cache on VisualGroup during transform to prevent desync
        if (visualGroupRef.current) {
           visualGroupRef.current.clearCache();
        }

        const node = e.target;
        // The dummy rect is inside the RootGroup, so its coords are relative to RootGroup
        // But we want to update the clippedGroupRef which is inside VisualGroup
        // VisualGroup is at 0,0 of RootGroup.
        
        if (clippedGroupRef.current) {
          // Mirror scale & rotation
          clippedGroupRef.current.scaleX(node.scaleX());
          clippedGroupRef.current.scaleY(node.scaleY());
          clippedGroupRef.current.rotation(node.rotation());

          // Align inner group to visual top-left of the transform target
          // Since DummyRect and VisualGroup are siblings at 0,0, this logic might simplify?
          // Wait, DummyRect has x/y = center. VisualGroup has x/y = 0.
          // clippedGroupRef is inside VisualGroup.
          
          // Existing logic adapted:
           const parent = node.getParent(); // This is RootGroup
           if (parent) {
             const rectAbs = node.getClientRect({ skipStroke: true });
             // We need position relative to VisualGroup (which is at 0,0 of RootGroup)
             // So relative to RootGroup is fine.
             const invParentTransform = parent.getAbsoluteTransform().copy().invert();
             const topLeftLocal = invParentTransform.point({ x: rectAbs.x, y: rectAbs.y });
             
             // Update clippedGroupRef (inside VisualGroup)
             clippedGroupRef.current.x(topLeftLocal.x);
             clippedGroupRef.current.y(topLeftLocal.y);
           }
        }
        eventHandlers.onTransform?.(e);
      },
      onTransformEnd: (e: any) => {
        eventHandlers.onTransformEnd?.(e);
        if (clippedGroupRef.current) {
          // Reset inner group (parent Group will be repositioned by store update)
          clippedGroupRef.current.x(0);
          clippedGroupRef.current.y(0);
          clippedGroupRef.current.scaleX(1);
          clippedGroupRef.current.scaleY(1);
          clippedGroupRef.current.rotation(0);
        }
        // Cache will be restored by useSmartCache after render
      },
    })
  );
};

/**
 * Hook for rendering elements using the element registry
 */
export const useElementRenderer = () => {
  const selectElement = useTemplateStore(state => state.selectElement);
  const updateElement = useTemplateStore(state => state.updateElement);
  const template = useTemplateStore(state => state.template);
  const { can } = usePermissions();

  /**
   * Render an element using the element registry
   */
  const renderElement = (element: any, eventHandlers: any) => {
    const definition = elementRegistry.get(element.type);
    if (!definition) return null;
  
    // Create enhanced resolve context with template slots for default value resolution
    const context: RenderContext = { 
      assetHost: "localhost:9001",
      resolveContext: { 
        selected: {}, 
        overrides: {},
        templateSlots: template?.slots || []
      }
    };
    const configs = definition.konvaRenderer(element, context);
    if (!configs || configs.length === 0) return null;
    
    const config = configs[0];
    
    // Check for watermark lock
    const isWatermarkLocked = element.isWatermark && !can(Permission.EDIT_WATERMARKS);
    const isEffectiveLocked = element.locked || isWatermarkLocked;
    
    const enhancedConfig: any = {
      ...config,
      id: element.id,
      x: element.x + element.width / 2,
      y: element.y + element.height / 2,
      offsetX: element.width / 2,
      offsetY: element.height / 2,
      onClick: isEffectiveLocked ? undefined : () => selectElement(element.id),
      onTap: isEffectiveLocked ? undefined : () => selectElement(element.id),
      onContextMenu: (e: any) => {
        // Select element on right click to ensure context menu acts on this element
        selectElement(element.id);
        // We don't prevent default here to allow the event to bubble to the ContextMenuTrigger
      },
      onDragMove: isEffectiveLocked ? undefined : eventHandlers.onDragMove,
      onDragEnd: isEffectiveLocked ? undefined : eventHandlers.onDragEnd,
      onTransform: isEffectiveLocked ? undefined : eventHandlers.onTransform,
      onTransformEnd: isEffectiveLocked ? undefined : eventHandlers.onTransformEnd,
      draggable: !isEffectiveLocked,
    };

    // Apply Effects (Shadow & Blur)
    if (element.effects && element.effects.length > 0) {
      const dropShadow = element.effects.find((e: any) => e.type === 'drop-shadow');
      if (dropShadow) {
        // Resolve shadow color
        const colorVal = resolveStaticOrDynamicWithPreview(dropShadow.color, context.resolveContext!) || '#000000';
        enhancedConfig.shadowColor = colorVal;
        enhancedConfig.shadowBlur = dropShadow.blur;
        enhancedConfig.shadowOffsetX = dropShadow.x;
        enhancedConfig.shadowOffsetY = dropShadow.y;
        enhancedConfig.shadowOpacity = dropShadow.opacity;
      }

      const layerBlur = element.effects.find((e: any) => e.type === 'layer-blur');
      if (layerBlur) {
         enhancedConfig.filters = [Konva.Filters.Blur];
         enhancedConfig.blurRadius = layerBlur.radius;
         // Ref interceptor REMOVED to avoid performance issues.
         // Caching is now handled by ShapeRenderer / ImageElementRenderer / EditableText
      }
    }

    // Apply Advanced Fill (Solid / Gradient)
    // This logic overrides the simple 'fill' property handled by the registry
    if (element.fill) {
        if (element.fill.kind === 'solid') {
             const colorVal = resolveStaticOrDynamicWithPreview(element.fill.color, context.resolveContext!) || '#000000';
             const opacity = element.fill.opacity ?? 1;
             // We use a helper to convert to rgba if opacity < 1, otherwise just hex
             // (Simple hex-to-rgba implementation inline or helper)
             const finalColor = opacity < 1 ? hexToRgba(colorVal as string, opacity) : colorVal;
             enhancedConfig.fill = finalColor;
             enhancedConfig.fillPriority = 'color';
        } else if (element.fill.kind === 'gradient') {
             const { gradientType, angle = 90, stops } = element.fill;
             
             // Resolve Stops
             // Konva expects: [0, "red", 0.5, "blue", 1, "green"]
             const colorStops: any[] = [];
             stops.forEach((stop: any) => {
                 const colorVal = resolveStaticOrDynamicWithPreview(stop.color, context.resolveContext!) || '#000000';
                 const finalColor = (stop.opacity ?? 1) < 1 ? hexToRgba(colorVal as string, stop.opacity ?? 1) : colorVal;
                 colorStops.push(stop.offset, finalColor);
             });
             
             if (gradientType === 'linear') {
                 enhancedConfig.fillPriority = 'linear-gradient';
                 enhancedConfig.fillLinearGradientColorStops = colorStops;
                 
                 // Calculate Start/End based on angle (in degrees)
                 // CSS 90deg = Left to Right. 0deg = Bottom to Top.
                 // We map this to coordinates on the rect (width, height)
                 // Simple approximation for Rect/Circle bounding box
                 const w = element.width;
                 const h = element.height;

                 const normalizedAngle = ((Number(angle) % 360) + 360) % 360;
                 const rad = ((normalizedAngle - 90) * Math.PI) / 180;
                 const cos = Math.cos(rad);
                 const sin = Math.sin(rad);

                 const isCenterOrigin = element.type === 'circle';

                 if (isCenterOrigin) {
                   const rx = w / 2;
                   const ry = h / 2;
                   const r = Math.sqrt(rx * rx + ry * ry);
                   enhancedConfig.fillLinearGradientStartPoint = { x: -cos * r, y: -sin * r };
                   enhancedConfig.fillLinearGradientEndPoint = { x: cos * r, y: sin * r };
                 } else {
                   const cx = w / 2;
                   const cy = h / 2;
                   const r = Math.sqrt(w * w + h * h) / 2;
                   enhancedConfig.fillLinearGradientStartPoint = { x: cx - cos * r, y: cy - sin * r };
                   enhancedConfig.fillLinearGradientEndPoint = { x: cx + cos * r, y: cy + sin * r };
                 }
             } else {
                 // Radial
                 enhancedConfig.fillPriority = 'radial-gradient';
                 enhancedConfig.fillRadialGradientColorStops = colorStops;
                 const isCenterOrigin = element.type === 'circle';
                 enhancedConfig.fillRadialGradientStartPoint = isCenterOrigin ? { x: 0, y: 0 } : { x: element.width / 2, y: element.height / 2 };
                 enhancedConfig.fillRadialGradientEndPoint = isCenterOrigin ? { x: 0, y: 0 } : { x: element.width / 2, y: element.height / 2 };
                 enhancedConfig.fillRadialGradientStartRadius = 0;
                 enhancedConfig.fillRadialGradientEndRadius = Math.max(element.width, element.height) / 2;
             }
        }
    }

    switch (element.type) {
      case 'rect':
        return React.createElement(ShapeRenderer, {
          key: element.id,
          ShapeComponent: Rect,
          config: enhancedConfig
        });
      case 'circle':
        // Fix for Transformer Issue:
        // Wrap Circle in a Group to handle Offset logic correctly.
        // Group behaves like a Rect (Origin TopLeft via Offset), while Inner Ellipse is centered.
        
        // Root Props (Positioning & Events)
        // Group has offset = width/2 to mimic Rect behavior for Transformer/Store
        const circleGroupProps = {
            x: enhancedConfig.x,
            y: enhancedConfig.y,
            offsetX: element.width / 2,
            offsetY: element.height / 2,
            width: element.width,
            height: element.height,
            rotation: enhancedConfig.rotation,
            opacity: enhancedConfig.opacity,
            draggable: enhancedConfig.draggable,
            onClick: enhancedConfig.onClick,
            onTap: enhancedConfig.onTap,
            onDragMove: enhancedConfig.onDragMove,
            onDragEnd: enhancedConfig.onDragEnd,
            onTransform: enhancedConfig.onTransform,
            onTransformEnd: enhancedConfig.onTransformEnd,
            onContextMenu: enhancedConfig.onContextMenu,
            id: enhancedConfig.id
        };

        // Inner Circle Props
        // Centered in Group (at width/2, height/2)
        // No offset, origin is center
        const innerCircleConfig = {
             ...enhancedConfig,
             x: element.width / 2,
             y: element.height / 2,
             offsetX: 0,
             offsetY: 0,
             width: element.width,
             height: element.height,
             radiusX: element.width / 2,
             radiusY: element.height / 2,
             // Remove root-level props from inner shape to avoid duplicate event handling
             draggable: false,
             onClick: undefined,
             onTap: undefined,
             onDragMove: undefined,
             onDragEnd: undefined,
             onTransform: undefined,
             onTransformEnd: undefined,
             onContextMenu: undefined,
             rotation: 0, // Rotation is handled by Group
             id: undefined // ID is on Group
        };

        return React.createElement(
            Group,
            { key: element.id, ...circleGroupProps },
            React.createElement(ShapeRenderer, {
                ShapeComponent: Ellipse,
                config: innerCircleConfig
            })
        );
      case 'text':
        return React.createElement(EditableText, {
          key: element.id,
          element,
          onUpdate: updateElement,
          ...enhancedConfig
        });
      case 'image': {
        // For images, transform handlers are handled inside the dummy rect
        const imageEnhancedConfig = {
          ...enhancedConfig,
          onTransform: undefined,
          onTransformEnd: undefined,
        };
        return React.createElement(ImageElementRenderer, {
          element,
          enhancedConfig: imageEnhancedConfig,
          resolveContext: context.resolveContext!,
          eventHandlers,
        });
      }
      case 'svg':
        return React.createElement(SvgRenderer, { 
          element: element as any, 
          resolveContext: context.resolveContext!, 
          eventHandlers,
          config: enhancedConfig
        });
      default:
        console.warn(`Unknown element type for rendering: ${element.type}`);
        return null;
    }
  };

  return { renderElement };
};
